import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { SUBMIT_GUARANTOR } from 'graphql/resolvers/admin/candidates/mutations';
import FormUploader from 'components/Uploader';
import { GUARANTOR_SUBMISSION } from 'graphql/resolvers/client/queries';
import { guarantorSubmitValidationSchema } from 'helpers/validators';
import { toastSuccess, toastError } from 'components/common/Toast';
import Loader from 'components/common/Loader';
import { svgs } from 'assets';

// Updated Component Name to match function
const GuarantorForm = ({
  match: { params },
  employeeName = 'The Candidate',
  prevTab
}) => {
  const requestId = params.id;
  const [showLoader, setLoaderState] = useState(false);

  // Ensure your mutation accepts the updated fields as per the 'formData' string
  const [submitGuarantorForm] = useMutation(SUBMIT_GUARANTOR);

  const { data: submissionData } = useQuery(GUARANTOR_SUBMISSION, {
    variables: {
      guarantorId: requestId
    },
    skip: !requestId
  });

  const isReadOnly = !!submissionData?.guarantorSubmission;

  const parsedExtraData = submissionData?.guarantorSubmission?.formData
    ? JSON.parse(submissionData?.guarantorSubmission?.formData)
    : {};

  // Layout Styles designed for a structured, grid-based form
  const styles = {
    wrapper: { background: '#f8fafc', minHeight: '100vh', padding: '40px 10px' },
    card: {
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      maxWidth: '1000px',
      margin: '0 auto',
      background: '#fff'
    },
    header: {
      padding: '30px 30px',
      borderBottom: '2px solid #e2e8f0',
      textAlign: 'center',
      background: '#ffffff'
    },
    mainTitle: {
      // fontSize: '3.5rem', // Largest text
      fontWeight: '900',
      letterSpacing: '2px',
      color: '#0f172a',
      textTransform: 'uppercase'
    },
    subText: {
      fontSize: '1.5rem', // Large and bold subtext
      fontWeight: '700',
      color: '#475569',
      marginTop: '10px'
    },
    formBody: { padding: '40px' },
    questionCell: { border: '1px solid #cbd5e1', padding: '15px' },
    label: { fontWeight: '600', color: '#1e293b' },
    input: {
      padding: '12px',
      borderRadius: '6px',
      border: '1px solid #cbd5e1',
      fontSize: '1.3rem'
    },
    radioOption: {
      border: '1px solid #cbd5e1',
      borderRadius: '6px',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    requiredAsterisk: { color: 'red', marginLeft: '3px' }
  };

  // 1. Precise Option Mapping from the Image
  const options = {
    // 3. Income Range (Nigerian Naira 'N')
    income: [
      'N2M to N5M Per annum',
      'N5M to N7M Per annum',
      'N7M to N9M Per annum',
      'N9m to N12M Per annum',
      'N12m to N15M Per annum',
      'Above N15M Per annum'
    ],

    // 4. Relationship
    relationship: ['Professional', 'Family', 'Friend', 'Others'],

    // 5. Duration known
    duration: ['Less than 3years', '3-5 years', '5-10 years', 'Over 10 years'],

    // 6. Character
    character: ['Very good', 'Good', 'Satisfactory', 'Unsatisfactory'],

    // 7. Honesty
    honest: ['Definitely', 'To a large extent', 'Not Certain', 'No Comment'],

    // 8. Recommend
    recommend: ['Yes', 'No'],

    // 9. Chosen to stand
    chosenToStand: ['Yes', 'No']
  };

  const handleOnSubmit = async (values) => {
    setLoaderState(true);
    try {
      const response = await submitGuarantorForm({
        variables: {
          input: {
            requestId: requestId,
            fullname: values.guarantorName,
            email: values.email,
            phone: values.phone,
            address: values.workAddress,
            idCard: values.idCard,
            // Map remaining 9 assessment questions into formData JSON string
            formData: JSON.stringify({
              guarantorFullName: values.guarantorName,
              workPlaceNameAddress: values.workAddress,
              incomeRange: values.incomeRange,
              relationship: values.relationship,
              relationshipOthers: values.relationshipOthers,
              durationKnown: values.durationKnown,
              durationComments: values.durationComments,
              conductCharacter: values.conductCharacter,
              conductComments: values.conductComments,
              isHonest: values.isHonest,
              recommendEmployment: values.recommendEmployment,
              recommendComments: values.recommendComments,
              chosenToStand: values.chosenToStand,
              generalComments: values.generalComments
            })
          }
        }
      });

      if (response.data?.submitGuarantorForm?.ok) {
        toastSuccess(svgs.SUCCESS_ICON, 'Guarantor Form Submitted Successfully');
      } else {
        const errors = response.data?.submitGuarantorForm?.errors?.messages.join(', ');
        toastError(svgs.DELETE_ICON, errors || 'Submission failed.');
      }
    } catch (e) {
      const errors = e?.data?.submitGuarantorForm?.errors?.messages.join(', ');
      toastError(svgs.DELETE_ICON, errors || 'An error occurred during submission.');
    } finally {
      setLoaderState(false);
    }
  };

  return (
    <div style={styles.wrapper}>
      <Formik
        enableReinitialize
        initialValues={{
          // Physical Form Mapping
          guarantorName: submissionData?.guarantorSubmission?.fullname || '',
          workAddress: submissionData?.guarantorSubmission?.address || '',
          incomeRange: parsedExtraData.incomeRange || '',
          relationship: parsedExtraData.relationship || '',
          relationshipOthers: '', // Specify field for Q4
          durationKnown: parsedExtraData.durationKnown || '',
          durationComments: parsedExtraData.durationComments || '',
          conductCharacter: parsedExtraData.conductCharacter || '',
          conductComments: parsedExtraData.conductComments || '',
          isHonest: parsedExtraData.isHonest || '',
          recommendEmployment: parsedExtraData.recommendEmployment || '',
          recommendComments: parsedExtraData.recommendComments || '',
          chosenToStand: parsedExtraData.chosenToStand || '',
          generalComments: parsedExtraData.generalComments || '',
          idCard: null,
          phone: submissionData?.guarantorSubmission?.phone || '',
          email: submissionData?.guarantorSubmission?.email || '',
          agreement: false // Checkbox control for certification
        }}
        validationSchema={guarantorSubmitValidationSchema}
        onSubmit={handleOnSubmit}
      >
        {({ values, setFieldValue, isSubmitting, isValid, dirty }) => (
          <div className="container px-0">
            <div className="card" style={styles.card}>
              <div style={styles.header}>
                <h1 style={styles.mainTitle}>GUARANTOR FORM</h1>
                <p style={styles.subText}>
                  All information provided would be treated with high level of
                  confidentiality.
                </p>
              </div>

              <Form style={styles.formBody}>
                {/* 1. Guarantor Name */}
                <div className="row mb-1">
                  <div className="col-12" style={styles.questionCell}>
                    <label style={styles.label}>
                      Guarantor’s Full Name:{' '}
                      <span style={styles.requiredAsterisk}>*</span>
                    </label>
                    <Field
                      name="guarantorName"
                      disabled={isReadOnly}
                      className="form-control mt-2"
                      style={styles.input}
                    />
                    <ErrorMessage
                      name="guarantorName"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </div>
                </div>
                {/* 2. Place of Work */}
                <div className="row mb-1">
                  <div className="col-12" style={styles.questionCell}>
                    <label style={styles.label}>
                      Current Place of Work/Business Name & Address?{' '}
                      <span style={styles.requiredAsterisk}>*</span>
                    </label>
                    <Field
                      name="workAddress"
                      disabled={isReadOnly}
                      as="textarea"
                      rows="3"
                      className="form-control mt-2"
                      style={styles.input}
                    />
                    <ErrorMessage
                      name="workAddress"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </div>
                </div>
                {/* Guarantor Certification and Contact Block */}
                <div className="row mb-1">
                  <div className="col-md-6" style={styles.questionCell}>
                    <label style={styles.label}>
                      Telephone Number <span style={styles.requiredAsterisk}>*</span>
                    </label>
                    <Field
                      name="phone"
                      className="form-control"
                      style={styles.input}
                      disabled={isReadOnly}
                      placeholder="080XXXXXXXX"
                    />
                    <ErrorMessage
                      name="phone"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </div>
                  <div className="col-md-6" style={styles.questionCell}>
                    <label style={styles.label}>
                      Email Address <span style={styles.requiredAsterisk}>*</span>
                    </label>
                    <Field
                      name="email"
                      type="email"
                      disabled={isReadOnly}
                      className="form-control"
                      style={styles.input}
                      placeholder="guarantor@example.com"
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </div>
                </div>
                {/* 3. Income Range */}
                <div className="row mb-1">
                  <div className="col-12" style={styles.questionCell}>
                    <label style={styles.label} className="mb-2">
                      Guarantor’s Income Range (Please tick as appropriate){' '}
                      <span style={styles.requiredAsterisk}>*</span>
                    </label>
                    <div className="row g-2">
                      {options.income.map((opt) => (
                        <div key={opt} className="col-md-6 col-lg-4">
                          <label
                            style={styles.radioOption}
                            className={`w-100 p-3 h-100 d-flex align-items-center ${
                              values.incomeRange === opt
                                ? 'bg-primary text-white'
                                : 'bg-white'
                            }`}
                          >
                            <Field
                              type="radio"
                              name="incomeRange"
                              value={opt}
                              disabled={isReadOnly}
                              className="d-none"
                            />
                            {opt}
                          </label>
                        </div>
                      ))}
                    </div>
                    <ErrorMessage
                      name="incomeRange"
                      component="div"
                      className="text-danger small mt-2"
                    />
                  </div>
                </div>
                {/* 4. Relationship */}
                <div className="row mb-1">
                  <div className="col-12" style={styles.questionCell}>
                    <label style={styles.label} className="mb-2">
                      Relationship with the employee{' '}
                      <span style={styles.requiredAsterisk}>*</span>
                    </label>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {options.relationship.map((opt) => (
                        <label
                          key={opt}
                          style={styles.radioOption}
                          className={`px-4 py-3 ${
                            values.relationship === opt
                              ? 'bg-primary text-white'
                              : 'bg-white'
                          }`}
                        >
                          <Field
                            type="radio"
                            disabled={isReadOnly}
                            name="relationship"
                            value={opt}
                            className="d-none"
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                    {/* Conditionally Require and Show "Others specify" if "Others" is selected */}
                    {values.relationship === 'Others' && (
                      <div className="col-md-6 mt-2">
                        <label className="text-muted small mb-1">(please specify)</label>
                        <Field
                          name="relationshipOthers"
                          className="form-control"
                          disabled={isReadOnly}
                          style={styles.input}
                        />
                        <ErrorMessage
                          name="relationshipOthers"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </div>
                    )}
                    <ErrorMessage
                      name="relationship"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </div>
                </div>
                {/* 5. How Long Known */}
                <div className="row mb-1">
                  <div className="col-12" style={styles.questionCell}>
                    <label style={styles.label} className="mb-2">
                      How long have you known the Candidate?{' '}
                      <span style={styles.requiredAsterisk}>*</span>
                    </label>
                    <p className="small text-danger fw-bold mb-2">
                      Do not stand as a guarantor for somebody you do not know
                    </p>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {options.duration.map((opt) => (
                        <label
                          key={opt}
                          style={styles.radioOption}
                          className={`px-4 py-3 ${
                            values.durationKnown === opt
                              ? 'bg-primary text-white'
                              : 'bg-white'
                          }`}
                        >
                          <Field
                            type="radio"
                            name="durationKnown"
                            value={opt}
                            disabled={isReadOnly}
                            className="d-none"
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                    <ErrorMessage
                      name="durationKnown"
                      component="div"
                      className="text-danger small mb-3"
                    />
                    <label className="text-muted small mb-1">Comments:</label>
                    <Field
                      name="durationComments"
                      as="textarea"
                      rows="2"
                      className="form-control"
                      disabled={isReadOnly}
                      style={styles.input}
                    />
                  </div>
                </div>
                {/* 6. Conduct/Character */}
                <div className="row mb-1">
                  <div className="col-12" style={styles.questionCell}>
                    <label style={styles.label} className="mb-2">
                      Conduct /Character and Association{' '}
                      <span style={styles.requiredAsterisk}>*</span>
                    </label>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {options.character.map((opt) => (
                        <label
                          key={opt}
                          style={styles.radioOption}
                          className={`px-4 py-3 ${
                            values.conductCharacter === opt
                              ? 'bg-primary text-white'
                              : 'bg-white'
                          }`}
                        >
                          <Field
                            type="radio"
                            name="conductCharacter"
                            value={opt}
                            disabled={isReadOnly}
                            className="d-none"
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                    <ErrorMessage
                      name="conductCharacter"
                      component="div"
                      className="text-danger small mb-3"
                    />
                    <label className="text-muted small mb-1">Comments:</label>
                    <Field
                      name="conductComments"
                      as="textarea"
                      rows="2"
                      disabled={isReadOnly}
                      className="form-control"
                      style={styles.input}
                    />
                  </div>
                </div>
                {/* 7. Honesty */}
                <div className="row mb-1">
                  <div className="col-12" style={styles.questionCell}>
                    <label style={styles.label} className="mb-2">
                      Do you consider him/ her to be Honest?{' '}
                      <span style={styles.requiredAsterisk}>*</span>
                    </label>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {options.honest.map((opt) => (
                        <label
                          key={opt}
                          style={styles.radioOption}
                          className={`px-4 py-3 ${
                            values.isHonest === opt ? 'bg-primary text-white' : 'bg-white'
                          }`}
                        >
                          <Field
                            type="radio"
                            name="isHonest"
                            value={opt}
                            disabled={isReadOnly}
                            className="d-none"
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                    <ErrorMessage
                      name="isHonest"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </div>
                </div>
                {/* 8. Recommendation */}
                <div className="row mb-1">
                  <div className="col-12" style={styles.questionCell}>
                    <label style={styles.label} className="mb-2">
                      Would you recommend him/her For employment with us and why?{' '}
                      <span style={styles.requiredAsterisk}>*</span>
                    </label>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {options.recommend.map((opt) => (
                        <label
                          key={opt}
                          style={styles.radioOption}
                          className={`px-4 py-3 ${
                            values.recommendEmployment === opt
                              ? 'bg-primary text-white'
                              : 'bg-white'
                          }`}
                        >
                          <Field
                            type="radio"
                            name="recommendEmployment"
                            value={opt}
                            disabled={isReadOnly}
                            className="d-none"
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                    <ErrorMessage
                      name="recommendEmployment"
                      component="div"
                      className="text-danger small mb-3"
                    />
                    <label className="text-muted small mb-1">Comments:</label>
                    <Field
                      name="recommendComments"
                      as="textarea"
                      rows="2"
                      disabled={isReadOnly}
                      className="form-control"
                      style={styles.input}
                    />
                  </div>
                </div>
                {/* 9. Chosen to Stand */}
                <div className="row mb-1 pb-4">
                  <div className="col-12" style={styles.questionCell}>
                    <label style={styles.label} className="mb-2">
                      You have chosen to stand as his/her guarantor (Please select your
                      choice) <span style={styles.requiredAsterisk}>*</span>
                    </label>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {options.chosenToStand.map((opt) => (
                        <label
                          key={opt}
                          style={styles.radioOption}
                          className={`px-4 py-3 ${
                            values.chosenToStand === opt
                              ? 'bg-primary text-white'
                              : 'bg-white'
                          }`}
                        >
                          <Field
                            type="radio"
                            name="chosenToStand"
                            value={opt}
                            disabled={isReadOnly}
                            className="d-none"
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                    <ErrorMessage
                      name="chosenToStand"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </div>
                </div>
                {/* General Comments */}
                <div className="row mb-5 pb-3">
                  <div className="col-12">
                    <label style={styles.label}>General Comment:</label>
                    <Field
                      name="generalComments"
                      as="textarea"
                      rows="5"
                      className="form-control mt-2"
                      style={styles.input}
                      disabled={isReadOnly}
                      placeholder="Provide any final general remarks here..."
                    />
                  </div>
                </div>

                <div className="col-12 mt-4 p-3 border-top border-dashed">
                  <label style={styles.label}>
                    {isReadOnly
                      ? 'Attached Identification'
                      : "Upload valid means of identification (only government issued ids are acceptable, e.g, NIN, driver's license, intl. passport, Voter's card)"}
                    <span style={styles.requiredAsterisk}>*</span>
                  </label>
                  {isReadOnly ? (
                    /* READ-ONLY VIEW: Show a button to view the existing document */
                    <div className="d-flex align-items-center p-3 border rounded bg-white">
                      <div className="me-3">
                        <span role="img" aria-label="" style={{ fontSize: '1.5rem' }}>
                          📄
                        </span>
                      </div>
                      <div className="flex-grow-1">
                        <p className="mb-0 fw-bold text-primary">Document</p>
                        <small className="text-muted">
                          Click the button to view the uploaded file
                        </small>
                      </div>
                      <a
                        href={submissionData?.referenceSubmission?.idCardUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline-primary btn-sm px-4 fw-bold"
                      >
                        VIEW DOCUMENT
                      </a>
                    </div>
                  ) : (
                    <FormUploader
                      accept={[
                        'image/png',
                        'image/jpg',
                        'image/jpeg',
                        'application/pdf',
                        'application/msword',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        'application/vnd.ms-powerpoint',
                        'application/vnd.openxmlformats-officedocument.presentationml.presentation'
                      ]}
                      handleUpload={(files) => setFieldValue('idCard', files[0])}
                      text="Choose File (ID Card or Signed Form)"
                      file={values.idCard}
                      form
                    />
                  )}
                  <ErrorMessage
                    name="idCard"
                    component="div"
                    className="text-danger small mt-2"
                  />
                </div>

                {/* Final Certification & Submit */}
                {!isReadOnly && (
                  <div className="mt-5 pt-4 border-top">
                    <div className="p-4 rounded border bg-light mb-4">
                      <div className="d-flex align-items-start">
                        <Field
                          type="checkbox"
                          name="agreement"
                          className="form-check-input me-3 mt-1"
                          id="agreementCheck"
                          style={{
                            width: '20px',
                            height: '20px',
                            flexShrink: 0,
                            cursor: 'pointer'
                          }}
                        />
                        <label
                          className="form-check-label fw-bold text-dark"
                          htmlFor="agreementCheck"
                          style={{ cursor: 'pointer', lineHeight: '1.5' }}
                        >
                          I hereby state that all information provided above are true and
                          I confirm that I stand as a Guarantor for {employeeName}.{' '}
                          <span style={styles.requiredAsterisk}>*</span>
                        </label>
                      </div>
                      <ErrorMessage
                        name="agreement"
                        component="div"
                        className="text-danger small mt-2 ms-5"
                      />
                    </div>

                    <div className="d-flex justify-content-end gap-3">
                      <button
                        type="submit"
                        className="btn btn-primary px-5 py-3 fw-bold"
                        // Disabled until form is valid, dirty, and agreement is checked
                        // disabled={
                        //   showLoader ||
                        //   isSubmitting ||
                        //   !values.agreement ||
                        //   !isValid ||
                        //   !dirty
                        // }
                      >
                        {showLoader ? <Loader /> : 'SUBMIT GUARANTOR FORM'}
                      </button>
                    </div>
                  </div>
                )}
              </Form>
            </div>
          </div>
        )}
      </Formik>
    </div>
  );
};

export default GuarantorForm;
